import jsPDF from 'jspdf';
import { CartItem } from '../../types';

interface TicketData {
  numeroPedido: string;
  fecha: Date;
  items: CartItem[];
  subtotal: number;
  importeEnvio: number;
  tarifaServicio: number;
  total: number;
  direccionEntrega: string;
  telefono: string;
  metodoPago: string;
  nombreNegocio: string;
}

export function generateTicketPdf(data: TicketData): void {
  // Ticket-style: 80mm width ≈ 226pt, variable height
  const pageWidth = 80; // mm
  const margin = 6;
  const contentWidth = pageWidth - margin * 2;
  let y = 10;

  // Estimate height based on items
  const estimatedHeight = 160 + data.items.length * 14;
  const doc = new jsPDF({ unit: 'mm', format: [pageWidth, estimatedHeight] });

  const centerText = (text: string, yPos: number, size: number, style: 'normal' | 'bold' = 'normal') => {
    doc.setFontSize(size);
    doc.setFont('helvetica', style);
    const textWidth = doc.getTextWidth(text);
    doc.text(text, (pageWidth - textWidth) / 2, yPos);
  };

  const drawDashedLine = (yPos: number) => {
    doc.setLineDashPattern([1, 1], 0);
    doc.setDrawColor(180);
    doc.setLineWidth(0.2);
    doc.line(margin, yPos, pageWidth - margin, yPos);
  };

  // === Header ===
  centerText('STITCH DELIVERY', y, 14, 'bold');
  y += 5;
  centerText('Ticket de Pedido', y, 9, 'normal');
  y += 7;
  drawDashedLine(y);
  y += 5;

  // === Order number & date ===
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text(`Pedido:`, margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(`${data.numeroPedido}`, margin + 14, y);
  y += 4;

  doc.setFont('helvetica', 'bold');
  doc.text(`Fecha:`, margin, y);
  doc.setFont('helvetica', 'normal');
  const fechaStr = data.fecha.toLocaleDateString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  doc.text(fechaStr, margin + 14, y);
  y += 4;

  doc.setFont('helvetica', 'bold');
  doc.text(`Negocio:`, margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(data.nombreNegocio, margin + 16, y);
  y += 5;

  drawDashedLine(y);
  y += 5;

  // === Delivery info ===
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.text('ENTREGA', margin, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  const addressLines = doc.splitTextToSize(data.direccionEntrega || 'Sin dirección', contentWidth);
  doc.text(addressLines, margin, y);
  y += addressLines.length * 3.2;

  if (data.telefono) {
    doc.text(`Tel: ${data.telefono}`, margin, y);
    y += 4;
  }

  doc.setFontSize(7);
  doc.text(`Pago: ${data.metodoPago}`, margin, y);
  y += 5;

  drawDashedLine(y);
  y += 5;

  // === Items header ===
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('Cant.', margin, y);
  doc.text('Producto', margin + 10, y);
  doc.text('Importe', pageWidth - margin, y, { align: 'right' });
  y += 2;
  drawDashedLine(y);
  y += 4;

  // === Items ===
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  data.items.forEach((item) => {
    const lineTotal = item.price * item.quantity;
    doc.text(`${item.quantity}`, margin + 2, y, { align: 'center' });

    const nameMaxWidth = contentWidth - 24;
    const nameLines = doc.splitTextToSize(item.name, nameMaxWidth);
    doc.text(nameLines[0], margin + 10, y);
    doc.text(`$${lineTotal.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });

    if (nameLines.length > 1) {
      y += 3.2;
      doc.text(nameLines[1], margin + 10, y);
    }

    y += 4.5;
  });

  y += 1;
  drawDashedLine(y);
  y += 5;

  // === Totals ===
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text('Subtotal:', margin, y);
  doc.text(`$${data.subtotal.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  y += 4;

  if (data.importeEnvio > 0) {
    doc.text('Envío:', margin, y);
    doc.text(`$${data.importeEnvio.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
    y += 4;
  }

  if (data.tarifaServicio > 0) {
    doc.text('Tarifa de servicio:', margin, y);
    doc.text(`$${data.tarifaServicio.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
    y += 4;
  }

  y += 1;
  drawDashedLine(y);
  y += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', margin, y);
  doc.text(`$${data.total.toFixed(2)}`, pageWidth - margin, y, { align: 'right' });
  y += 7;

  drawDashedLine(y);
  y += 6;

  // === Footer ===
  centerText('¡Gracias por tu pedido!', y, 8, 'bold');
  y += 4;
  centerText('Stitch Delivery', y, 7, 'normal');

  // Save / download
  doc.save(`ticket-${data.numeroPedido}.pdf`);
}
